import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const ADD_SENTINEL = "__ADD_NEW__";

/**
 * Drop-in <select> replacement that lets the user add custom values without
 * leaving the form. Stores additions under /api/options/{domain}.
 *
 * Props:
 *  - domain:       string key for /api/options/{domain} (e.g. "crop", "species")
 *  - defaults:     built-in options. Either ["Corn","Soybeans"] or [{value,label}]
 *  - value:        current selected value
 *  - onChange:     (newValue) => void
 *  - testid:       data-testid for the <select>
 *  - required:     html required attr
 *  - placeholder:  optional "— Select —" first option label
 *  - className:    pass-through CSS for the select
 */
export default function AddableSelect({
  domain,
  defaults = [],
  value,
  onChange,
  testid,
  required,
  placeholder,
  className = "w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm",
}) {
  const [custom, setCustom] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const normalize = (opts) =>
    opts.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const normalizedDefaults = useMemo(() => normalize(defaults), [defaults]);

  const reload = async () => {
    try {
      const { data } = await api.get(`/options/${domain}`);
      setCustom(data);
    } catch {
      // non-fatal — keep defaults
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  // Merge defaults + custom, dedupe by value
  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of [...normalizedDefaults, ...custom.map((c) => ({ value: c.value, label: c.label }))]) {
      if (seen.has(o.value)) continue;
      seen.add(o.value);
      out.push(o);
    }
    return out;
  }, [normalizedDefaults, custom]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === ADD_SENTINEL) {
      setAdding(true);
      setNewLabel("");
      return;
    }
    onChange(v);
  };

  const saveNew = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Enter a name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/options/${domain}`, { value: label, label });
      await reload();
      onChange(data.value);
      toast.success(`"${label}" added`);
      setAdding(false);
      setNewLabel("");
    } catch {
      toast.error("Could not save option");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testid || domain}-wrap`}>
      <select
        data-testid={testid}
        required={required}
        value={value || ""}
        onChange={handleChange}
        className={className}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {merged.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        <option value={ADD_SENTINEL}>+ Add new…</option>
      </select>

      {adding && (
        <div className="bg-[#FAF7EC] border border-[#1F1E1B] p-2 flex items-center gap-2" data-testid={`${testid || domain}-add-panel`}>
          <input
            data-testid={`${testid || domain}-add-input`}
            autoFocus
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); saveNew(); }
              if (e.key === "Escape") { setAdding(false); setNewLabel(""); }
            }}
            placeholder="Type new value and press Enter"
            className="flex-1 bg-white border border-[#1F1E1B] px-2 py-1 text-sm"
          />
          <button
            type="button"
            data-testid={`${testid || domain}-add-save`}
            onClick={saveNew}
            disabled={saving}
            className="bg-[#1A4135] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            data-testid={`${testid || domain}-add-cancel`}
            onClick={() => { setAdding(false); setNewLabel(""); }}
            className="border border-[#1F1E1B] px-3 py-1 text-xs font-bold uppercase tracking-wider"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
